/**
 * parse middleware — 终结器（fetch + JSON 解包）+ envelope 解包。
 *
 * 设计要点：
 *  - parse 是"最里层"，在 createClient 里总是排在 middleware 数组末尾。
 *    它不再 await next()（next 永远是 parse 之外的占位），而是自己跑 fetch。
 *  - 错误码来源优先级：
 *      1. body.error.code（如 'INVALID_CREDENTIALS'）
 *      2. status === 401 → 'UNAUTHORIZED'
 *      3. status >= 500 → 'INTERNAL'
 *      4. 非 JSON / 异常响应 → 'INVALID_RESPONSE'
 *      5. 网络异常 → 'NETWORK'
 *  - 上层（features/*）拿到的就是 ApiResponseContext，由终结器层负责
 *    把 status === 200 && !data && errorCode 不为空时抛 ApiError。
 *    抛 ApiError 仍由 client.ts 处理（保持兼容）。
 *  - fetch 必须用 live reference（`globalThis.fetch` 在 MSW 监听后才被 patch，
 *    若在 module init 时 bind 会捕获未 patch 的版本；用 `() => fetch(...)` 避免）。
 */

import type { ApiCallContext, ApiResponseContext, Middleware } from './types';

export interface ParseMiddlewareOptions {
  /** baseUrl 字符串（固定）或 getter 函数（live —— 业务侧改 client.baseUrl 后自动跟随） */
  baseUrl: string | (() => string);
  /** 让测试可注入自定义 fetch；默认走 globalThis.fetch（live） */
  fetchImpl?: typeof fetch;
  /** 自定义 body 读取函数（默认 response.json()）；测试可注入 */
  readJson?: (resp: Response) => Promise<unknown>;
  /** 把 ApiCallContext → RequestInit；测试可覆盖 */
  buildInit?: (ctx: ApiCallContext) => RequestInit;
}

interface FetchResult {
  status: number;
  ok: boolean;
  headers: Record<string, string>;
  payload: unknown;
  parseError: 'none' | 'empty' | 'invalid_json';
}

async function performFetch(
  ctx: ApiCallContext,
  resolveBaseUrl: string | (() => string),
  doFetch: typeof fetch,
  buildInit: (c: ApiCallContext) => RequestInit,
  readJson: (r: Response) => Promise<unknown>,
): Promise<FetchResult> {
  const base = typeof resolveBaseUrl === 'function' ? resolveBaseUrl() : resolveBaseUrl;
  const url = base.replace(/\/$/, '') + ctx.path;
  const init = buildInit(ctx);

  let response: Response;
  try {
    response = await doFetch(url, init);
  } catch {
    return { status: 0, ok: false, headers: {}, payload: undefined, parseError: 'none' };
  }

  const headers: Record<string, string> = {};
  try {
    response.headers.forEach((v, k) => {
      headers[k.toLowerCase()] = v;
    });
  } catch {
    /* noop */
  }

  // 204 No Content — 不读 body。
  if (response.status === 204) {
    return { status: response.status, ok: response.ok, headers, payload: undefined, parseError: 'empty' };
  }

  let payload: unknown;
  try {
    payload = await readJson(response);
  } catch {
    return { status: response.status, ok: response.ok, headers, payload: undefined, parseError: 'invalid_json' };
  }
  return { status: response.status, ok: response.ok, headers, payload, parseError: 'none' };
}

/**
 * 工厂：构造一个绑定 baseUrl 的 parse middleware（终结器 + envelope 解包）。
 */
export function createParseMiddleware(opts: ParseMiddlewareOptions): Middleware {
  const readJson = opts.readJson ?? (async (r: Response) => r.json());
  const buildInit = opts.buildInit ?? ((ctx: ApiCallContext): RequestInit => {
    const init: RequestInit = {
      method: ctx.method,
      headers: ctx.headers,
      signal: ctx.signal,
    };
    if (ctx.body !== undefined) {
      init.body = JSON.stringify(ctx.body);
    }
    return init;
  });

  return async function parseMiddleware(ctx): Promise<ApiResponseContext> {
    // live fetch：每次都从 globalThis.fetch 取，让 MSW / 测试桩拦截生效。
    const fn: typeof fetch = opts.fetchImpl
      ?? (typeof fetch !== 'undefined'
        ? (...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => fetch(...args)
        : ((): ReturnType<typeof fetch> => { throw new Error('fetch unavailable'); }) as unknown as typeof fetch);
    const result = await performFetch(ctx, opts.baseUrl, fn, buildInit, readJson);

    if (result.status === 0) {
      return { status: 0, ok: false, headers: {}, errorCode: 'NETWORK' };
    }

    // 收集 schema version（后端通过 X-Schema-Version header 返回）。
    const schemaVersion = result.headers['x-schema-version'];

    // 204 No Content。
    if (result.parseError === 'empty' || result.status === 204) {
      return { status: result.status, ok: true, headers: result.headers, schemaVersion };
    }

    if (result.parseError === 'invalid_json') {
      return { status: result.status, ok: false, headers: result.headers, errorCode: 'INVALID_RESPONSE', schemaVersion };
    }

    const payload = result.payload;
    const envOk = payload && typeof payload === 'object' && (payload as { ok?: unknown }).ok;

    if (result.ok && envOk === true) {
      const data = (payload as { data?: unknown }).data;
      return { status: result.status, ok: true, headers: result.headers, data, schemaVersion };
    }

    // 失败路径：拆 envelope → errorCode / message（message 仅用于 telemetry 之外的逻辑）。
    const env = payload as { error?: { code?: string; message?: string } } | undefined;
    let errorCode = env?.error?.code;
    if (!errorCode) {
      if (result.status === 401) errorCode = 'UNAUTHORIZED';
      else if (result.status >= 500) errorCode = 'INTERNAL';
      else errorCode = 'INVALID_RESPONSE';
    }
    return { status: result.status, ok: false, headers: result.headers, errorCode, schemaVersion };
  };
}
